/**
 * API工具函数
 * 提供一些常用的API辅助功能
 */

import { API_CONFIG } from './config'

/**
 * 构建查询参数字符串
 * @param {Object} params - 参数对象
 * @returns {string} 查询参数字符串
 */
export function buildQueryString(params) {
  if (!params || typeof params !== 'object') {
    return ''
  }

  const queryString = Object.keys(params)
    .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== '')
    .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
    .join('&')

  return queryString ? `?${queryString}` : ''
}

/**
 * 格式化文件大小
 * @param {number} bytes - 字节数
 * @returns {string} 格式化后的文件大小
 */
export function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

/**
 * 创建下载链接并触发下载
 * @param {string} url - 下载链接
 * @param {string} filename - 文件名
 * @param {Function} onSuccess - 成功回调
 * @param {Function} onError - 错误回调
 */
export function downloadFile(url, filename, onSuccess, onError) {
  try {
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `download_${Date.now()}`
    link.style.display = 'none'

    // 添加错误处理
    link.addEventListener('error', () => {
      if (onError) onError('下载链接无法访问')
      document.body.removeChild(link)
    })

    // 添加成功处理
    link.addEventListener('click', () => {
      setTimeout(() => {
        if (onSuccess) onSuccess('下载已开始')
        document.body.removeChild(link)
      }, 100)
    })

    document.body.appendChild(link)
    link.click()
  } catch (error) {
    if (onError) onError(error.message)
  }
}

/**
 * 下载音频文件
 * @param {string} audioUrl - 音频URL路径 (如：/audio/filename.wav)
 * @param {Function} onSuccess - 成功回调
 * @param {Function} onError - 错误回调
 */
export function downloadAudio(audioUrl, onSuccess, onError) {
  try {
    // 参数验证：检查audioUrl是否为有效字符串
    if (!audioUrl || typeof audioUrl !== 'string') {
      const errorMsg = audioUrl === null || audioUrl === undefined
        ? '音频URL为空，无法下载'
        : `音频URL格式错误，期望字符串类型，实际收到: ${typeof audioUrl}`
      console.error('下载音频失败:', errorMsg, audioUrl)
      if (onError) onError(errorMsg)
      return
    }

    // 检查URL是否包含路径分隔符
    if (!audioUrl.includes('/')) {
      const errorMsg = `音频URL格式无效: ${audioUrl}`
      console.error('下载音频失败:', errorMsg)
      if (onError) onError(errorMsg)
      return
    }

    // 从audioUrl中提取文件名
    const filename = audioUrl.split('/').pop()

    // 验证文件名是否有效
    if (!filename || filename.trim() === '') {
      const errorMsg = `无法从URL中提取有效文件名: ${audioUrl}`
      console.error('下载音频失败:', errorMsg)
      if (onError) onError(errorMsg)
      return
    }

    // 构建完整的下载URL，添加download=true参数
    const downloadUrl = `${API_CONFIG.BASE_URL}/voice/audio/${filename}?download=true`

    // 生成唯一文件名
    const downloadFilename = `synthesis_${Date.now()}.wav`

    downloadFile(downloadUrl, downloadFilename, onSuccess, onError)
  } catch (error) {
    console.error('下载音频时发生异常:', error)
    if (onError) onError(error.message || '下载失败')
  }
}

/**
 * 将Blob转换为下载链接
 * @param {Blob} blob - Blob对象
 * @param {string} filename - 文件名
 */
export function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob)
  downloadFile(url, filename)
  window.URL.revokeObjectURL(url)
}

/**
 * 防抖函数
 * @param {Function} func - 要防抖的函数
 * @param {number} wait - 等待时间（毫秒）
 * @returns {Function} 防抖后的函数
 */
export function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

/**
 * 节流函数
 * @param {Function} func - 要节流的函数
 * @param {number} limit - 时间间隔（毫秒）
 * @returns {Function} 节流后的函数
 */
export function throttle(func, limit) {
  let inThrottle
  return function executedFunction(...args) {
    if (!inThrottle) {
      func.apply(this, args)
      inThrottle = true
      setTimeout(() => inThrottle = false, limit)
    }
  }
}

/**
 * 重试函数
 * @param {Function} fn - 要重试的异步函数
 * @param {number} retries - 重试次数
 * @param {number} delay - 重试延迟（毫秒）
 * @returns {Promise} Promise对象
 */
export async function retry(fn, retries = 3, delay = 1000) {
  try {
    return await fn()
  } catch (error) {
    if (retries > 0) {
      await new Promise(resolve => setTimeout(resolve, delay))
      return retry(fn, retries - 1, delay)
    }
    throw error
  }
}

/**
 * 格式化日期时间
 * @param {Date|string|number} date - 日期
 * @param {string} format - 格式字符串
 * @returns {string} 格式化后的日期字符串
 */
export function formatDateTime(date, format = 'YYYY-MM-DD HH:mm:ss') {
  const d = new Date(date)
  if (isNaN(d.getTime())) {
    return ''
  }

  const year = d.getFullYear()
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  const hours = String(d.getHours()).padStart(2, '0')
  const minutes = String(d.getMinutes()).padStart(2, '0')
  const seconds = String(d.getSeconds()).padStart(2, '0')

  return format
    .replace('YYYY', year)
    .replace('MM', month)
    .replace('DD', day)
    .replace('HH', hours)
    .replace('mm', minutes)
    .replace('ss', seconds)
}

/**
 * 验证邮箱格式
 * @param {string} email - 邮箱地址
 * @returns {boolean} 是否为有效邮箱
 */
export function isValidEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

/**
 * 验证手机号格式（中国大陆）
 * @param {string} phone - 手机号
 * @returns {boolean} 是否为有效手机号
 */
export function isValidPhone(phone) {
  const phoneRegex = /^1[3-9]\d{9}$/
  return phoneRegex.test(phone)
}



// WEBPACK FOOTER //
// ./src/api/utils.js